import React, { useEffect, useState } from "react";
import axios from "axios";
import { SCHOOL_QUIZ_API_BASE_URL as API_BASE_URL } from "../config"; // adjust path as needed
import { useDispatch, useSelector } from "react-redux";
import {
  addOrUpdateSubject,
  deleteSubject,
  fetchSubjects,
  setSubjectForm,
} from "./redux/schoolQuizSlice";

export default function ThemesTable() {
  // Data states
  const dispatch = useDispatch();
  const { subjectsData, subjectsList } = useSelector(
    (state) => state.schoolQuiz
  );
  // const { subjects, subject_id, subject_name, open, mode } = subjectsData;
  const [themes, setThemes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Modal/UI states
  const [modalOpen, setModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingTheme, setEditingTheme] = useState(null);

  // Form states
  const [formName, setFormName] = useState("");
  const [formSubthemes, setFormSubthemes] = useState([{ id: null, name: "" }]);

  // Fetch themes
  const fetchThemes = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API_BASE_URL}/themes`);
      setThemes(res.data || []);
    } catch (err) {
      setError("Failed to fetch data");
      console.error("fetchThemes error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchThemes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    dispatch(fetchSubjects());
  }, [dispatch]);

  // Open modal for add
  const openAddModal = () => {
    setModalOpen(true);
    setEditMode(false);
    setEditingTheme(null);
    setFormName("");
    setFormSubthemes([{ id: null, name: "" }]);
  };

  // Open modal for edit
  const openEditModal = (theme) => {
    setModalOpen(true);
    setEditMode(true);
    setEditingTheme(theme);
    setFormName(theme.name || "");
    setFormSubthemes(
      theme.sub_theme && theme.sub_theme.length > 0
        ? theme.sub_theme.map((s) => ({ id: s.theme_id, name: s.name }))
        : [{ id: null, name: "" }]
    );
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditMode(false);
    setEditingTheme(null);
  };

  // Form handlers
  const updateSubthemeName = (index, value) => {
    const updated = [...formSubthemes];
    updated[index] = { ...updated[index], name: value };
    setFormSubthemes(updated);
  };

  const addSubthemeInput = () => {
    setFormSubthemes([...formSubthemes, { id: null, name: "" }]);
  };

  const removeSubthemeInput = (index) => {
    const updated = [...formSubthemes];
    updated.splice(index, 1);
    setFormSubthemes(updated.length ? updated : [{ id: null, name: "" }]);
  };

  // Submit add/edit
  const submitForm = async (e) => {
    e.preventDefault();
    if (!formName.trim()) return alert("Please enter theme name.");

    // Normalize formSubthemes → backend expects `theme_id` for existing rows
    const cleanedSubthemes = (formSubthemes || [])
      .map((s) => ({
        theme_id: s.id ?? null, // existing child id (or null for new)
        name: (s.name || "").trim(),
      }))
      .filter((s) => s.name.length > 0);

    setLoading(true);
    try {
      if (editMode) {
        const idParam = editingTheme?.theme_id ?? editingTheme?.id;
        if (!idParam) throw new Error("Missing theme id on editingTheme");

        await axios.put(`${API_BASE_URL}/update-themes/${idParam}`, {
          name: formName.trim(),
          sub_theme: cleanedSubthemes, // includes theme_id for existing, just name for new
        });
      } else {
        await axios.post(`${API_BASE_URL}/add-themes`, {
          name: formName.trim(),
          sub_theme: cleanedSubthemes.map((s) => ({ name: s.name })), // only names when creating
        });
      }

      await fetchThemes();
      closeModal();
    } catch (err) {
      alert(err.response?.data?.message || err.message || "Operation failed");
      console.error("submitForm error:", err);
    } finally {
      setLoading(false);
    }
  };

  const submitFormNew = async (e) => {
    e.preventDefault();

    if (!subjectsData.subject_name.trim()) {
      return alert("Please enter subject name.");
    }

    setLoading(true);

    try {
      const mode = subjectsData.mode;
      const subject_id = mode !== "Add" ? subjectsData.subject_id : null; // or whatever your editing ID is
      const subject_name = subjectsData.subject_name.trim();

      // 🔹 Dispatch the thunk
      const resultAction = await dispatch(
        addOrUpdateSubject({ subject_id, subject_name, mode })
      );

      // 🔹 Check if it succeeded
      if (addOrUpdateSubject.fulfilled.match(resultAction)) {
        dispatch(
          setSubjectForm({
            key: "open",
            value: false,
          })
        );
        await dispatch(fetchSubjects()); // refresh list
      } else {
        alert(resultAction.payload || "Operation failed");
      }
    } catch (err) {
      console.error("submitFormNew error:", err);
      alert(err.message || "Operation failed");
    } finally {
      setLoading(false);
    }
  };

  const deleteTheme = async (theme) => {
    if (!window.confirm(`Delete theme "${theme.name}"?`)) return;
    setLoading(true);
    try {
      await axios.delete(`${API_BASE_URL}/delete-themes/${theme.theme_id}`);
      await fetchThemes();
    } catch (err) {
      alert(err.response?.data?.message || err.message || "Delete failed");
    } finally {
      setLoading(false);
    }
  };

  const deleteSubjectId = async (value) => {
    const subject_id = value;
    setLoading(true);
    console.log(value);

    try {
      await dispatch(deleteSubject({ subject_id }));
      await dispatch(fetchSubjects());
    } catch (err) {
      console.error("deletion  error:", err);
      alert(err.message || "Operation failed");
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="container px-0">
      {/* Left Panel - Question Selection */}
      <div className="left-panel">
        <div>
          <div style={{ display: "flex", gap: "40%", marginLeft: "40%" }}>
            {/* <h5 style={{ textAlign: "center" }}>Groups</h5> */}

            <h5 style={{ textAlign: "center" }}>Themes</h5>
            <button className="btn btn-primary btn-sm" onClick={openAddModal}>
              + Add Theme
            </button>
          </div>

          <br />
          {error && <div className="alert alert-danger">{error}</div>}

          <div className="table-responsive">
            <table className="table mb-0 table-striped">
              <thead>
                <tr>
                  <th style={{ width: 80 }}>S.No</th>
                  <th>Theme</th>
                  <th>Subthemes</th>
                  <th style={{ width: 160 }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {loading && themes.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="text-center p-4">
                      Loading...
                    </td>
                  </tr>
                ) : themes.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="text-center p-4">
                      No themes yet.
                    </td>
                  </tr>
                ) : (
                  themes.map((t, i) => (
                    <tr key={t.theme_id || i}>
                      <td>{i + 1}</td>
                      <td>{t.name}</td>
                      <td>
                        {t.sub_theme?.length === 0 ? (
                          <small className="text-muted">—</small>
                        ) : (
                          t.sub_theme.map((s, idx) => (
                            <span
                              key={s.theme_id || idx}
                              className="badge bg-light text-dark me-1"
                            >
                              {s.name}
                            </span>
                          ))
                        )}
                      </td>
                      <td>
                        <div className="d-flex gap-2">
                          <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => openEditModal(t)}
                          >
                            Edit
                          </button>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => deleteTheme(t)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Modal */}
          {modalOpen && (
            <div style={modalBackdrop}>
              <div style={modalDialog}>
                <h5 className="mb-3">
                  {editMode ? "Edit Theme" : "Add Theme"}
                </h5>
                <form onSubmit={submitForm}>
                  <div className="mb-2">
                    <label className="form-label small">Theme</label>
                    <input
                      className="form-control"
                      value={formName}
                      onChange={(e) => setFormName(e.target.value)}
                      placeholder="Theme name"
                    />
                  </div>

                  <div className="mb-2">
                    <label className="form-label small">Subthemes</label>
                    {formSubthemes.map((s, idx) => (
                      <div key={idx} className="d-flex gap-2 mb-2">
                        <input
                          className="form-control"
                          placeholder={`Subtheme ${idx + 1}`}
                          value={s.name}
                          onChange={(e) =>
                            updateSubthemeName(idx, e.target.value)
                          }
                        />
                        <button
                          type="button"
                          className="btn btn-outline-danger"
                          onClick={() => removeSubthemeInput(idx)}
                          title="Remove"
                        >
                          🗑
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm"
                      onClick={addSubthemeInput}
                    >
                      + Add another subtheme
                    </button>
                  </div>

                  <div className="d-flex justify-content-end gap-2 mt-3">
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={closeModal}
                    >
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary btn-sm">
                      {editMode ? "Save" : "Add"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="right-panel" style={{ height: "80vh", overflow: "auto" }}>
        <div style={{ display: "flex", gap: "40%", marginLeft: "40%" }}>
          <h5 style={{ textAlign: "center" }}>Subjects</h5>
          <button
            className="btn btn-primary btn-sm"
            onClick={() => {
              dispatch(setSubjectForm({ key: "open", value: true }));
              dispatch(setSubjectForm({ key: "subject_name", value: "" }));
              dispatch(setSubjectForm({ key: "mode", value: "Add" }));
            }}
            style={{ float: "right" }}
          >
            + Add Subject
          </button>
        </div>
        <br />
        <div className="table-responsive">
          <table className="table mb-0 table-striped">
            <thead>
              <tr>
                <th style={{ width: 80 }}>S.No</th>
                <th>Subject Name</th>
                <th style={{ width: 160 }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {loading && subjectsList.length === 0 ? (
                <tr>
                  <td colSpan={4} className="text-center p-4">
                    Loading...
                  </td>
                </tr>
              ) : !loading && subjectsList.length === 0 ? (
                <tr>
                  <td colSpan={4} className="text-center p-4">
                    No classes yet.
                  </td>
                </tr>
              ) : (
                subjectsList?.data
                  ?.filter((e) => e.status === 1)
                  .map((g, i) => {
                    return (
                      <tr key={g.id ?? i}>
                        <td>{i + 1}</td>
                        <td>{g.subject_name}</td>

                        <td>
                          <div className="d-flex gap-2">
                            <button
                              className="btn btn-sm btn-outline-secondary"
                              onClick={() => {
                                dispatch(
                                  setSubjectForm({ key: "open", value: true })
                                );
                                dispatch(
                                  setSubjectForm({
                                    key: "subject_name",
                                    value: g.subject_name,
                                  })
                                );
                                dispatch(
                                  setSubjectForm({
                                    key: "subject_id",
                                    value: g.subject_id,
                                  })
                                );
                                dispatch(
                                  setSubjectForm({
                                    key: "mode",
                                    value: "Update",
                                  })
                                );
                              }}
                            >
                              Edit
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => {
                                deleteSubjectId(g.subject_id);
                              }}
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
              )}
            </tbody>
          </table>
        </div>
        {subjectsData.open && (
          <div style={modalBackdrop}>
            <div style={modalDialog}>
              <h5 className="mb-3" style={{ textAlign: "center" }}>
                {subjectsData.mode} Subject
              </h5>
              <form onSubmit={submitFormNew}>
                {/* <form> */}
                <div className="mb-2">
                  <label className="form-label small">Subject</label>
                  <input
                    className="form-control"
                    value={subjectsData.subject_name}
                    onChange={(e) =>
                      dispatch(
                        setSubjectForm({
                          key: "subject_name",
                          value: e.target.value,
                        })
                      )
                    }
                    placeholder="Subject  name"
                  />
                </div>

                <div className="d-flex justify-content-end gap-2 mt-3">
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => {
                      dispatch(
                        setSubjectForm({
                          key: "open",
                          value: false,
                        })
                      );
                    }}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary btn-sm">
                    {/* {subjectsData.mode} */}
                    {subjectsData.mode !== "Add" ? "Update" : "Add"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Minimal inline modal styles
const modalBackdrop = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.32)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1200,
  padding: 12,
};

const modalDialog = {
  width: "100%",
  maxWidth: 560,
  background: "#fff",
  padding: 16,
  borderRadius: 6,
  boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
};
